/**
 * sharedb client code
 */
import { NCBoard, UserFieldName } from '../types';
import ReconnectingWebSocket from 'reconnecting-websocket';
import * as sharedb from 'sharedb/lib/client';
import { Graph, InspireGraph, IdeateGraph, MoodboardGraph } from './graphs';
import {
	startLoading,
	finishLoading,
	closeAllPanels,
	handleHashRoute,
	setDefaultBoardSettings,
} from './ui/uiUtils';
// @ts-expect-error types not available
import richText from 'rich-text';
import { Socket, Type } from 'sharedb/lib/sharedb';
import { openHeartedList, refreshHeartedList } from './ui/ideaBox';
import { setSynthesiseDockMode } from './ui/synthesiseDock';
import { getAllUserSelections, getLabels } from './graphs/graphUtils';
import {
	extractGraphData,
	getGraphName,
	getInspireDataForIdeate,
	makeInspireDeleteCascadeListener,
	makeSaveClustersFunctions,
	makeSaveGraphFn,
	makeSaveGroupsFunctions,
	makeSaveLinksFunctions,
	makeSaveNodesFunctions,
	makeSavePositioningLayoutFn,
	makeSaveSingleUserFn,
	makeSaveUserAnalyticsFn,
} from './shareDbUtils';
import { AvailableColour, GraphMode, UserSelection } from '../../../src/commonTypes';
import { setActiveStage, setStageCompletionStatuses } from './ui/toolsUI';
import { closeAllMenus } from './ui/menuUi';
import { makeOpListener } from './shareDbListener';
import './chatbot/AgentChatPanel';
import {
	FLOWY_CONTEXT_MODE_CHANGING_EVENT,
	type FlowyContextModeChangingDetail,
	supportsPersonalTeamContext,
} from './chatbot/flowyContextMode';
import { tutorialEmitter } from './tutorials/tutorialEvents';
import { TUTORIAL_EVENT_TYPES } from './tutorials/tutorialEvents';

// Global variables
globalThis.neuroCreate = {
	graph: undefined,
	saveUsers: undefined,
	saveSingleUser: undefined,
	saveGraph: undefined,
	saveNodes: undefined,
	saveLinks: undefined,
	saveGroups: undefined,
	saveClusters: undefined,
	savePositioningLayout: undefined,
	saveUserAnalytics: undefined,
	switchToMode: undefined,
	doc: undefined,
	userFieldName: undefined,
	gapiLoaded: () => {
		console.log('will be replaced by slides.ts');
	},
	gisLoaded: () => {
		console.log('will be replaced by slides.ts');
	},
	automateTutorial: false,
};

const agentEventListeners = new Map<string, Set<(event: any) => void>>();

const getColourForCurrentUser = (): [colourClass: AvailableColour, hex: string] => {
	const classes = Array.from(
		document.getElementById('current-user-icon')!.classList.values()
	).filter((c) => c !== 'header-menu-icon');
	const colourClass = classes[0] as AvailableColour;
	return [colourClass, getComputedStyle(document.body).getPropertyValue(`--${colourClass}`).trim()];
};

if (document.getElementById('top-menu-wrapper')) {
	sharedb.types.register((richText as { type: Type }).type);

	const authToken = document.body.dataset.authToken as string;

	/**
	 * Open WebSocket connection to ShareDB server
	 */
	const socket: ReconnectingWebSocket = new ReconnectingWebSocket(
		`${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${
			window.location.host
		}?token=${authToken}`
	);
	const connection = new sharedb.Connection(socket as Socket);
	connection.on('receive', function (request) {
		// Custom message passing
		// @see https://github.com/share/sharedb/issues/104#issuecomment-240596912
		const data = request.data as {
			loggedOut?: boolean;
			agentEvent?: { type: string; sessionId: string; boardId: string; [key: string]: unknown };
		};
		if (data.loggedOut) {
			console.log('Logged out or token expired, reloading page');
			window.location.reload();
			request.data = null;
			return;
		}
		if (data.agentEvent) {
			const listeners = agentEventListeners.get(data.agentEvent.sessionId);
			listeners?.forEach((listener) => {
				listener(data.agentEvent!);
			});
			request.data = null;
		}
	});
	connection.on('connected', () => {
		finishLoading();
	});

	let isReconnecting = true,
		isConnectionLost = false;
	socket.addEventListener('close', (closeEvent) => {
		console.log('[socket close]', closeEvent);
		if (isReconnecting) {
			isConnectionLost = true;
			setTimeout(() => {
				if (isConnectionLost) {
					startLoading('Lost connection. Attempting to reconnect...');
				}
			}, 5000);
		}
	});
	socket.addEventListener('open', (event) => {
		console.log('[socket open]', event);
		isConnectionLost = false;
		startLoading('Connecting...');
	});
	socket.addEventListener('error', (err) => {
		console.log('[socket error]', err);
	});
	// socket.addEventListener('message', (event) => {
	// 	console.log(`[socket message]`, event);
	// });

	window.onbeforeunload = () => {
		// Ensure we do not show "Lost connection" message when navigating away
		isReconnecting = false;
	};

	// Loading indicator within notepad
	const notePadLoadingIndicator = document.querySelector('#notepad-container .loading')!;
	/**
	 * Create local Doc instances mapped to collection documents in database
	 */
	let doc: sharedb.Doc<NCBoard> | undefined, notesDoc: undefined | sharedb.Doc;
	let modeSwitchRequestId = 0;
	let pendingModeTeardown = Promise.resolve();
	let activeOpBatchListener: ((ops: Array<sharedb.Op>) => void) | undefined;
	let activeCascadeListener: ((ops: Array<sharedb.Op>) => void) | undefined;
	const DOC_DESTROY_TIMEOUT_MS = 3_000;
	const modeHash = (graphMode: GraphMode, isCollaborative: boolean): string => {
		if (graphMode === 'ideate') return isCollaborative ? '#ideate-team' : '#ideate';
		if (graphMode === 'moodboard') return isCollaborative ? '#synthesise-team' : '#synthesise';
		return '#inspire';
	};

	const clearActiveGraph = () => {
		if (globalThis.neuroCreate.graph) {
			globalThis.neuroCreate.graph.cleanUp();
			globalThis.neuroCreate.graph = undefined;
		}
	};

	const detachModeDocListeners = (modeDoc: sharedb.Doc<NCBoard>) => {
		if (activeOpBatchListener) {
			modeDoc.removeListener('op batch', activeOpBatchListener);
			activeOpBatchListener = undefined;
		}
		if (activeCascadeListener) {
			modeDoc.removeListener('op batch', activeCascadeListener);
			activeCascadeListener = undefined;
		}
	};

	const destroyShareDbDoc = (docToDestroy: sharedb.Doc): Promise<void> =>
		new Promise((resolve) => {
			let settled = false;
			const done = () => {
				if (settled) return;
				settled = true;
				resolve();
			};
			const timer = window.setTimeout(() => {
				console.warn('[ws] ShareDB doc destroy timed out; continuing mode switch');
				done();
			}, DOC_DESTROY_TIMEOUT_MS);
			try {
				docToDestroy.destroy(() => {
					window.clearTimeout(timer);
					console.log('doc destroyed');
					done();
				});
			} catch (error) {
				window.clearTimeout(timer);
				console.warn('[ws] ShareDB doc destroy failed', error);
				done();
			}
		});

	const bindModeDocListeners = (
		modeDoc: sharedb.Doc<NCBoard>,
		graph: Graph,
		userFieldName: UserFieldName,
		isCollaborative: boolean
	) => {
		detachModeDocListeners(modeDoc);
		activeOpBatchListener = makeOpListener(graph, userFieldName, isCollaborative);
		activeCascadeListener = makeInspireDeleteCascadeListener(modeDoc, userFieldName, () => {
			refreshHeartedList();
		});
		modeDoc.on('op batch', activeOpBatchListener);
		modeDoc.on('op batch', activeCascadeListener);
	};

	const mountGraphForMode = ({
		modeDoc,
		graphMode,
		isCollaborative,
		automation,
		userFieldName,
		userId,
		boardId,
	}: {
		modeDoc: sharedb.Doc<NCBoard>;
		graphMode: GraphMode;
		isCollaborative: boolean;
		automation: boolean;
		userFieldName: UserFieldName;
		userId: string;
		boardId: string;
	}): sharedb.Doc | undefined => {
		clearActiveGraph();

		globalThis.neuroCreate.saveSingleUser = makeSaveSingleUserFn(modeDoc);
		const graphData = extractGraphData(modeDoc, userFieldName, graphMode, isCollaborative);
		const [colourClass, colourHex] = getColourForCurrentUser();

		console.log(`Loading ${graphMode} graph with following data...`, graphData);

		const boardUsers = modeDoc.data.users;

		let userSelections: Array<UserSelection>;
		if (isCollaborative && graphMode === 'ideate') {
			userSelections = getAllUserSelections(modeDoc.data);
		} else {
			userSelections = modeDoc.data[userFieldName]?.userSelections || [];
		}

		let graph: Graph;
		let modeNotesDoc: sharedb.Doc | undefined;
		if (graphMode === 'inspire') {
			graph = new InspireGraph(
				graphData,
				boardUsers,
				userSelections,
				colourClass,
				colourHex,
				automation
			);
		} else if (graphMode === 'ideate') {
			const { inspireNodesToAnalyse, activeAnalysis, input, vision, summary, pdfSummary } =
				getInspireDataForIdeate(modeDoc, userFieldName, isCollaborative, colourClass, colourHex);
			graph = new IdeateGraph(
				graphData,
				boardUsers,
				userSelections,
				colourClass,
				getLabels(inspireNodesToAnalyse, true),
				activeAnalysis,
				isCollaborative,
				automation,
				input,
				vision,
				summary,
				pdfSummary
			);
		} else {
			modeNotesDoc = isCollaborative
				? connection.get('teamNotes', boardId)
				: connection.get('personalNotes', `${boardId}_${userId}`);
			graph = new MoodboardGraph(
				graphData,
				boardUsers,
				userSelections,
				colourClass,
				colourHex,
				isCollaborative,
				automation,
				modeNotesDoc
			);
		}

		globalThis.neuroCreate.graph = graph;
		setDefaultBoardSettings();
		tutorialEmitter.emit(TUTORIAL_EVENT_TYPES.switchBoard, { graphMode, isCollaborative });

		setTimeout(() => {
			notePadLoadingIndicator.classList.add('hidden');
		}, 500);

		setStageCompletionStatuses();
		bindModeDocListeners(modeDoc, graph, userFieldName, isCollaborative);

		globalThis.neuroCreate.saveGraph = makeSaveGraphFn(modeDoc, userFieldName, userId, isCollaborative);
		globalThis.neuroCreate.saveNodes = makeSaveNodesFunctions(
			modeDoc,
			userFieldName,
			userId,
			isCollaborative,
			graphMode
		);
		globalThis.neuroCreate.saveLinks = makeSaveLinksFunctions(
			modeDoc,
			userFieldName,
			userId,
			isCollaborative,
			graphMode
		);
		globalThis.neuroCreate.saveGroups = makeSaveGroupsFunctions(
			modeDoc,
			userFieldName,
			userId,
			isCollaborative,
			graphMode
		);
		globalThis.neuroCreate.saveClusters = makeSaveClustersFunctions(
			modeDoc,
			userFieldName,
			userId,
			isCollaborative,
			graphMode
		);
		globalThis.neuroCreate.savePositioningLayout = makeSavePositioningLayoutFn(
			modeDoc,
			userFieldName,
			userId,
			isCollaborative,
			graphMode
		);
		globalThis.neuroCreate.saveUserAnalytics = makeSaveUserAnalyticsFn(modeDoc, userFieldName);

		return modeNotesDoc;
	};

	const loadMode = (
		graphMode: GraphMode,
		isCollaborative = true,
		automation = false,
		requestId: number
	): Promise<void> => {
		return new Promise((resolve) => {
			const { boardId, projectionName, userId } = document.body.dataset;
			if (!boardId || !projectionName || !userId) {
				resolve();
				return;
			}
			if (requestId !== modeSwitchRequestId) {
				resolve();
				return;
			}

			const userFieldName = projectionName.replace('boardsUser', 'user') as UserFieldName;
			globalThis.neuroCreate.userFieldName = userFieldName;
			startLoading(`Loading ${getGraphName(graphMode)}`);

			const canReuseSubscribedBoardDoc =
				Boolean(doc?.data) && String(doc!.id) === String(boardId);

			if (canReuseSubscribedBoardDoc && doc) {
				if (requestId !== modeSwitchRequestId) {
					resolve();
					return;
				}
				finishLoading();
				notesDoc = mountGraphForMode({
					modeDoc: doc,
					graphMode,
					isCollaborative,
					automation,
					userFieldName,
					userId,
					boardId,
				});
				globalThis.neuroCreate.doc = doc;
				if (notesDoc) {
					notesDoc.once('load', () => {
						resolve();
					});
				} else {
					resolve();
				}
				return;
			}

			const modeDoc = connection.get(projectionName, boardId) as sharedb.Doc<NCBoard>;
			doc = modeDoc;
			globalThis.neuroCreate.doc = modeDoc;

			/**
			 * Get initial value of document and subscribe to changes.
			 * Set up graph with document data.
			 */
			modeDoc.subscribe(function (err) {
				if (err) {
					throw err;
				}
				if (requestId !== modeSwitchRequestId) {
					// A newer switch already owns the transition; do not leave a
					// cleaned-up graph instance registered as active.
					clearActiveGraph();
					resolve();
					return;
				}

				finishLoading();
				notesDoc = mountGraphForMode({
					modeDoc,
					graphMode,
					isCollaborative,
					automation,
					userFieldName,
					userId,
					boardId,
				});

				modeDoc.on('load', () => {
					console.log('[ShareDB] Document loaded');
				});
				modeDoc.on('create', () => {
					console.log('[ShareDB] Document create');
				});
				modeDoc.on('del', () => {
					console.log('[ShareDB] Document deleted');
				});
				modeDoc.on('error', (error) => {
					console.log('[ShareDB] error', error);
				});

				if (notesDoc) {
					notesDoc.once('load', () => {
						resolve();
					});
				} else {
					resolve();
				}
			});
		});
	};

	const synthesiseSwitch = document.querySelector<HTMLDivElement>('#synthesise-switch')!;
	synthesiseSwitch.querySelectorAll<HTMLInputElement>('input').forEach((radio) => {
		radio.onchange = (event: Event) => {
			if (globalThis.neuroCreate.graph) {
				const graphMode = globalThis.neuroCreate.graph.graphMode;
				const isCollaborative = (event.target as HTMLInputElement).value === 'team';
				if (graphMode === 'ideate') {
					window.location.hash = isCollaborative ? '#ideate-team' : '#ideate';
				} else if (graphMode === 'moodboard') {
					// Show loading indicator when user switches team or personal notepad
					notePadLoadingIndicator.classList.remove('hidden');
					window.location.hash = isCollaborative ? '#synthesise-team' : '#synthesise';
				}
			} else {
				event.preventDefault();
			}
		};
	});

	// const modeSwitcherSelect = document.querySelector<HTMLSelectElement>(
	// 	'#board-page-switcher select'
	// )!;

	/**
	 * Switch to a specific mode, either for personal or team
	 */
	globalThis.neuroCreate.switchToMode = (
		graphMode: GraphMode,
		isCollaborative: boolean,
		automation: boolean,
		afterLoad: (() => void | Promise<void>) | null = null
	): void => {
		const currentGraph = globalThis.neuroCreate.graph;
		const isPersonalTeamModeSwitch = Boolean(
			currentGraph &&
				currentGraph.graphMode === graphMode &&
				currentGraph.isCollaborative !== isCollaborative &&
				supportsPersonalTeamContext(graphMode)
		);
		if (isPersonalTeamModeSwitch) {
			window.dispatchEvent(
				new CustomEvent<FlowyContextModeChangingDetail>(FLOWY_CONTEXT_MODE_CHANGING_EVENT, {
					detail: { graphMode, isCollaborative },
				})
			);
		}
		const requestId = ++modeSwitchRequestId;
		if (!automation) {
			const nextHash = modeHash(graphMode, isCollaborative);
			if (window.location.hash !== nextHash) {
				window.history.replaceState(null, '', nextHash);
			}
		}
		// Before graph loads:
		// modeSwitcherSelect.value = graphMode;
		setActiveStage(graphMode);
		setSynthesiseDockMode(graphMode === 'moodboard');
		closeAllMenus();
		if (graphMode === 'ideate') {
			document.querySelector('#generate-container')?.classList.add('hidden');
		}

		if (graphMode !== 'ideate') {
			document.querySelector('#analyses-container')?.classList.add('hidden');
		} else {
			document.querySelector<HTMLInputElement>('input#ideate-use-ai-results')!.checked = true;
		}

		const inspireProgress = document.querySelector('#inspire-progress')!;
		const simulationMode = document.querySelector('#automode')!;
		if (graphMode === 'moodboard' || graphMode === 'ideate') {
			synthesiseSwitch.classList.remove('hidden');
			synthesiseSwitch.querySelectorAll('input')[isCollaborative ? 1 : 0].checked = true;
			inspireProgress.classList.add('hidden');
			if (simulationMode) {
				simulationMode.classList.add('hidden');
			}
		} else {
			synthesiseSwitch.classList.add('hidden');
			inspireProgress.classList.remove('hidden');
			if (simulationMode) {
				simulationMode.classList.remove('hidden');
			}
		}

		const generatePanel = document.querySelector<HTMLDivElement>('#generate-container');
		const searchPanel = document.querySelector<HTMLDivElement>('#search-container');
		if (graphMode === 'inspire') {
			generatePanel?.classList.add('inspire-mode');
			searchPanel?.classList.add('inspire-mode');
		} else {
			generatePanel?.classList.remove('inspire-mode');
			searchPanel?.classList.remove('inspire-mode');
		}

		const switchToModeImpl = async () => {
			if (requestId !== modeSwitchRequestId) return;
			await loadMode(graphMode, isCollaborative, automation, requestId);
			if (requestId !== modeSwitchRequestId) return;
			const notePadButton = document.querySelector<HTMLButtonElement>('#notepad-button')!;
			if (graphMode === 'moodboard') {
				notePadButton.classList.remove('hidden');
				openHeartedList(() => {
					console.log('opened hearted list');
					if (globalThis.neuroCreate.graph) {
						globalThis.neuroCreate.graph.validate();
					}
				});
			} else {
				closeAllPanels();
				notePadButton.classList.add('hidden');
			}

			const groupControls = document.querySelectorAll<HTMLButtonElement>('.group-control');
			const undoControls = document.querySelectorAll<HTMLButtonElement>('.undo-control');
			if (graphMode === 'ideate') {
				[...groupControls, ...undoControls].forEach((button) => {
					button.classList.add('hidden');
				});
			} else {
				[...groupControls, ...undoControls].forEach((button) => {
					button.classList.remove('hidden');
				});
			}

			if (afterLoad) {
				afterLoad();
			}
		};

		const { boardId } = document.body.dataset;
		const canReuseBoardDoc =
			isPersonalTeamModeSwitch &&
			Boolean(doc?.data) &&
			Boolean(boardId) &&
			String(doc!.id) === String(boardId);

		// Personal/team toggles read different paths in the same ShareDB board doc.
		// Destroying and re-subscribing that doc is unnecessary and can hang, leaving
		// Flowy updated while the painted board stays on the previous context.
		if (canReuseBoardDoc) {
			const notesDocToDestroy = notesDoc;
			notesDoc = undefined;
			const teardownTasks: Array<Promise<void>> = [pendingModeTeardown];
			if (notesDocToDestroy) {
				teardownTasks.push(destroyShareDbDoc(notesDocToDestroy));
			}
			pendingModeTeardown = Promise.all(teardownTasks).then(() => undefined);
			void pendingModeTeardown.then(() => {
				if (requestId === modeSwitchRequestId) void switchToModeImpl();
			});
			return;
		}

		const docToDestroy = doc;
		const notesDocToDestroy = notesDoc;
		doc = undefined;
		notesDoc = undefined;
		if (globalThis.neuroCreate.doc === docToDestroy) {
			globalThis.neuroCreate.doc = undefined;
		}
		if (docToDestroy) {
			detachModeDocListeners(docToDestroy);
		}
		clearActiveGraph();
		const teardownTasks: Array<Promise<void>> = [pendingModeTeardown];
		if (docToDestroy) {
			teardownTasks.push(destroyShareDbDoc(docToDestroy));
		}
		if (notesDocToDestroy) {
			teardownTasks.push(destroyShareDbDoc(notesDocToDestroy));
		}
		pendingModeTeardown = Promise.all(teardownTasks).then(() => undefined);
		void pendingModeTeardown.then(() => {
			if (requestId === modeSwitchRequestId) void switchToModeImpl();
		});

		// tutorialEmitter.emit(TUTORIAL_EVENT_TYPES.switchBoard, { graphMode, isCollaborative });
	};

	const mountAgentChatPanel = () => {
		const mountPoint = document.getElementById('agent-chat-panel-mount');
		const { boardId } = document.body.dataset;
		if (!mountPoint || !boardId) {
			return;
		}
		if (!mountPoint.querySelector('agent-chat-panel')) {
			const panel = document.createElement('agent-chat-panel');
			panel.setAttribute('board-id', boardId);
			mountPoint.appendChild(panel);
		}
	};

	// modeSwitcherSelect.selectedIndex = 0; // Reset to first value (in case browser has saved older state)
	// modeSwitcherSelect.onchange = (event) => {
	// 	const newMode = modeSwitcherSelect.value as GraphMode;
	// 	if (globalThis.neuroCreate.switchToMode) {
	// 		globalThis.neuroCreate.switchToMode(newMode, newMode === 'inspire', false);
	// 	} else {
	// 		event.preventDefault();
	// 	}
	// };

	// Load initial graph mode based on hash segment of URL
	mountAgentChatPanel();
	handleHashRoute();

	// Listen for future hash changes
	window.addEventListener('hashchange', () => {
		handleHashRoute();
	});
}
	window.subscribeAgentSession = (sessionId, onEvent) => {
		if (!agentEventListeners.has(sessionId)) {
			agentEventListeners.set(sessionId, new Set());
		}
		agentEventListeners.get(sessionId)!.add(onEvent);

		return () => {
			agentEventListeners.get(sessionId)?.delete(onEvent);
			if (agentEventListeners.get(sessionId)?.size === 0) {
				agentEventListeners.delete(sessionId);
			}
		};
	};
